import React, { useEffect, useState, useRef } from "react";
import { authFetch } from "../utils/authFetch";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import "../css/CandidatePreferences.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCheck,
  faInfoCircle,
  faSignOutAlt,
  faHome,
  faTimes,
  faList,
  faCalendarAlt,
  faFileUpload,
  faUpload,
  faSpinner,
  faFilePdf,
  faFileAlt,
  faSearch,
  faChevronDown,
  faEnvelope,
  faExclamationTriangle,
  faCreditCard,
  faEdit
} from "@fortawesome/free-solid-svg-icons";

// SearchableDropdown Component
function SearchableDropdown({ options, value, onChange, placeholder, disabled, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange({ target: { name: 'professor', value: option } });
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <label>{label}</label>
      <div 
        className={`dropdown-header ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={!value ? 'placeholder' : ''}>
          {value || placeholder}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
      </div>
      
      {isOpen && !disabled && (
        <div className="dropdown-content">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search professor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="options-list">
            <div 
              className={`dropdown-option ${value === '' ? 'selected' : ''}`}
              onClick={() => handleSelect('')}
            >
              All Professors
            </div>
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                className={`dropdown-option ${value === option ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="no-options">No professors found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CandidatePreferences() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modes, setModes] = useState([]);
  const [durations, setDurations] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [profilePhotoPath, setProfilePhotoPath] = useState("");
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [expectedJoiningDate, setExpectedJoiningDate] = useState("");
  const [isEditingJoiningDate, setIsEditingJoiningDate] = useState(false);
  const [isSubmittingDate, setIsSubmittingDate] = useState(false);
  const [dateSubmitMessage, setDateSubmitMessage] = useState({ text: '', type: '' });
  const [filters, setFilters] = useState({
    department: "",
    mode: "",
    duration: "",
    professor: "",
    search: "",
  });
  const [hasExistingReport, setHasExistingReport] = useState(false);
  const [checkingReport, setCheckingReport] = useState(false);
  const [reportFile, setReportFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [adminConf, setAdminConf] = useState(false);
  const [allottedProject, setAllottedProject] = useState(null);
  const [offerPaymentStatus, setOfferPaymentStatus] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [allottedProjectError, setAllottedProjectError] = useState(null);

  // Ref to track data fetching to prevent infinite loops
  const dataFetchedRef = useRef({
    paymentStatus: false,
    joiningDate: false,
    reportChecked: false,
    allottedProjectPayed: false
  });

  const navigate = useNavigate();

  // Check if project report already exists
  const checkExistingReport = async () => {
    if (adminConf && allottedProject && allottedProject.id) {
      setCheckingReport(true);
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch("/api/students/project_report", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          // If we get a 200 OK, the report exists
          setHasExistingReport(true);

          // Get the filename from the Content-Disposition header if needed
          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              // Store filename if you want to display it
              setAllottedProject(prev => ({
                ...prev,
                report_filename: filenameMatch[1].replace(/['"]/g, '')
              }));
            }
            console.log("Report filename from header:", filenameMatch ? filenameMatch[1] : "No filename found");
          }
        } else if (response.status === 404) {
          // No report found
          setHasExistingReport(false);
        }
      } catch (error) {
        console.error("Error checking existing report:", error);
        setHasExistingReport(false);
      } finally {
        setCheckingReport(false);
      }
    }
  };

  // Combined effect for payment status, joining date, and report check
  useEffect(() => {
    const fetchData = async () => {
      if (adminConf) {
        // Fetch payment status only once
        if (!dataFetchedRef.current.paymentStatus) {
          try {
            const paymentRes = await authFetch("/api/students/me/offer_payment_status");
            if (paymentRes.ok) {
              const paymentData = await paymentRes.json();
              setOfferPaymentStatus(paymentData.offer_payment_status);
              dataFetchedRef.current.paymentStatus = true;

              if (paymentData.offer_payment_status === "unpaid") {
                navigate("/payment2");
                return;
              }
              if (paymentData.offer_payment_status === "pending") {
                navigate("/pending");
                return;
              }
              if (paymentData.offer_payment_status === "success" || paymentData.offer_payment_status === "paid") {
                setPaymentCompleted(true);
                setPaymentFailed(false);
                


              } else if (paymentData.offer_payment_status === "failed") {
                setPaymentFailed(true);
                setPaymentCompleted(false);
              }
            } else {
              // If we can't fetch payment status, assume not paid
              dataFetchedRef.current.paymentStatus = true;
            }
          } catch (error) {
            console.error("Error checking payment status:", error);
            dataFetchedRef.current.paymentStatus = true;
          }
        }

        // If payment is completed, fetch the detailed allotted project with professor email
        if (paymentCompleted && !dataFetchedRef.current.allottedProjectPayed) {
          try {
            const allottedPayedRes = await authFetch("/api/students/me/allotted_project/payed");
            if (allottedPayedRes.ok) {
              const detailedProjectData = await allottedPayedRes.json();
              console.log("Fetched detailed allotted project after payment:", detailedProjectData);
              
              // Ensure professor email is properly set
              if (detailedProjectData.professor) {
                console.log("Professor email from API:", detailedProjectData.professor.email);
              } else {
                console.warn("No professor data in response");
              }
              
              setAllottedProject(detailedProjectData);
              dataFetchedRef.current.allottedProjectPayed = true;
              setAllottedProjectError(null);
            } else if (allottedPayedRes.status === 403) {
              const errorData = await allottedPayedRes.json();
              setAllottedProjectError(errorData.detail || "Please complete the offer payment to view project details");
              console.log("Offer payment not completed yet:", errorData);
            } else {
              console.error("Error fetching detailed allotted project");
              setAllottedProjectError("Failed to fetch project details");
            }
          } catch (error) {
            console.error("Error fetching detailed allotted project:", error);
            setAllottedProjectError("Network error while fetching project details");
          }
        }

        // Only fetch joining date and report if we have allotted project
        if (allottedProject && allottedProject.id) {
          // Fetch joining date only once
          if (!dataFetchedRef.current.joiningDate) {
            try {
              const response = await authFetch("/api/students/me/expected_joining_date");
              if (response.ok) {
                const data = await response.json();
                if (data.expected_joining_date) {
                  setAllottedProject(prev => ({
                    ...prev,
                    expected_joining_date: data.expected_joining_date
                  }));
                }
                dataFetchedRef.current.joiningDate = true;
              } else if (response.status === 404) {
                dataFetchedRef.current.joiningDate = true;
                console.log("No expected joining date set yet");
              } else {
                const errorData = await response.json();
                console.error("Error fetching expected joining date:", errorData);
                dataFetchedRef.current.joiningDate = true;
              }
            } catch (error) {
              console.error("Error fetching expected joining date:", error);
              dataFetchedRef.current.joiningDate = true;
            }
          }

          // Check for existing report only once
          if (!dataFetchedRef.current.reportChecked) {
             await checkExistingReport();
	     dataFetchedRef.current.reportChecked = true;
          }
        }
      }
    };

    fetchData();
  }, [adminConf, allottedProject, navigate, paymentCompleted]);

  // Handle expected joining date submission
  const handleSetExpectedJoiningDate = async () => {
    if (!expectedJoiningDate) {
      setDateSubmitMessage({
        text: 'Please select a joining date',
        type: 'error'
      });
      return;
    }

    setIsSubmittingDate(true);
    setDateSubmitMessage({ text: '', type: '' });

    try {
      const token = localStorage.getItem("access_token");

      // Format the date to YYYY-MM-DD
      const dateObj = new Date(expectedJoiningDate);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      console.log("Sending date to backend:", formattedDate);

      const response = await fetch("/api/students/me/expected_joining_date", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          date: formattedDate
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (response.status === 422) {
          console.log("Validation error:", errorData);
          let errorMessage = "Date format error. ";

          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              errorMessage += errorData.detail.map(err => err.msg).join(', ');
            } else if (typeof errorData.detail === 'string') {
              errorMessage += errorData.detail;
            } else {
              errorMessage += "Please use YYYY-MM-DD format.";
            }
          }
          throw new Error(errorMessage);
        } else {
          throw new Error(errorData.detail || "Failed to submit joining date");
        }
      }

      const data = await response.json();
      setDateSubmitMessage({
        text: data.message || 'Joining date submitted successfully!',
        type: 'success'
      });

      setAllottedProject(prev => ({
        ...prev,
        expected_joining_date: formattedDate
      }));

      // Clear the input and exit edit mode
      setExpectedJoiningDate('');
      setIsEditingJoiningDate(false);

    } catch (error) {
      console.error("Error setting joining date:", error);
      setDateSubmitMessage({
        text: error.message || 'Network error. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSubmittingDate(false);
    }
  };

  // Handle report upload
  const handleReportFileChange = (e) => {
    setReportFile(e.target.files[0]);
    setUploadMsg("");
    setUploadError("");
  };

  const handleReportUpload = async (e) => {
    e.preventDefault();
    setUploadMsg("");
    setUploadError("");

    if (!reportFile) {
      setUploadError("Please select a file to upload.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("project_report", reportFile);

      const token = localStorage.getItem("access_token");
      const res = await fetch("/api/students/project_report", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        setUploadMsg(data.message || "Report uploaded successfully!");
        setReportFile(null);

        // After successful upload, check for existing report again to update the UI
        setHasExistingReport(true);

        // Update filename from the uploaded file
        setAllottedProject(prev => ({
          ...prev,
          report_filename: reportFile.name,
          report_url: data.report_url || prev.report_url
        }));
      } else {
        setUploadError(data.detail || "Failed to upload report.");
      }
    } catch (err) {
      setUploadError("Failed to upload report. Please try again.");
    }
    setUploading(false);
  };

  // Main data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Check admin confirmation status first
        const adminConfRes = await authFetch("/api/students/admin_conf");
        if (adminConfRes.ok) {
          const adminConfData = await adminConfRes.json();
          setAdminConf(adminConfData.admin_conf);

          if (adminConfData.admin_conf) {
            // Don't fetch allotted project immediately - wait for payment
            setIsLoading(false);
            return;
          }
        }

        // If not admin confirmed, proceed with normal flow
        const candidateRes = await authFetch("/api/students/me");
        if (candidateRes.ok) {
          const candidateData = await candidateRes.json();
          setCandidate(candidateData);
        }

        const projectsRes = await authFetch("/api/students/all_projects");
        if (!projectsRes.ok) {
          throw new Error("Failed to load projects");
        }
        const projectsData = await projectsRes.json();
        console.log("Fetched projects:", projectsData);
        setProjects(projectsData);
        setFilteredProjects(projectsData);

        setDepartments([...new Set(projectsData.map((p) => p.department?.name).filter(name => name && name !== "N/A"))]);
        setModes([...new Set(projectsData.map((p) => p.mode || "N/A"))]);
        setDurations([...new Set(projectsData.map((p) => p.duration || "N/A"))]);
        setProfessors([...new Set(projectsData.map((p) => p.professor?.name || "N/A").filter(name => name !== "N/A"))]);

        const appliedRes = await authFetch("/api/students/preferences");
        if (appliedRes.ok) {
          const appliedData = await appliedRes.json();
          if (appliedData.length > 0) {
            setSelectedProjects(appliedData);
            setFilters((prev) => ({
              ...prev,
              department: appliedData[0].department?.name || "",
            }));
          }
        }
      } catch (err) {
        setMessage({
          text: err.message || "Failed to load data",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter projects based on filters
  useEffect(() => {
    const applyFilters = () => {
      let filtered = projects;

      if (filters.department) {
        filtered = filtered.filter(
          (project) => project.department?.name === filters.department
        );
      }

      if (filters.mode) {
        filtered = filtered.filter((project) => project.mode === filters.mode);
      }

      if (filters.duration) {
        filtered = filtered.filter((project) => project.duration === filters.duration);
      }

      if (filters.professor) {
        filtered = filtered.filter((project) => project.professor?.name === filters.professor);
      }

      if (filters.search) {
        filtered = filtered.filter((project) =>
          project.title.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setFilteredProjects(filtered);
    };

    applyFilters();
  }, [filters, projects]);

  // Profile photo effect
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      setIsPhotoLoading(true);
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const res = await authFetch("/api/students/profile_photo", {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          if (data.profile_photo_path) {
            const cleanedPath = data.profile_photo_path.startsWith("/")
              ? data.profile_photo_path.substring(1)
              : data.profile_photo_path;
            const fullUrl = `${window.location.origin}/${cleanedPath}`;
            setProfilePhotoPath(`${fullUrl}?${Date.now()}`);
          }
        } else if (res.status === 404) {
          setProfilePhotoPath("/images/default.png");
        }
      } catch (error) {
        setProfilePhotoPath("/images/default.png");
      } finally {
        setIsPhotoLoading(false);
      }
    };

    fetchProfilePhoto();
  }, []);

  // GSAP animations
  useEffect(() => {
    if (selectedProjects.length > 0) {
      gsap.from('.selected-item', {
        y: 20,
        opacity: 0,
        stagger: 0.1,
        duration: 0.3,
        ease: "power2.out"
      });
    }
  }, [selectedProjects.length]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      search: e.target.value,
    }));
  };

  const toggleProjectSelection = (project) => {
    setSelectedProjects((prev) => {
      const isSelected = prev.some((p) => p.id === project.id);
      if (isSelected) {
        return prev.filter((p) => p.id !== project.id);
      }

      if (prev.length >= 3) {
        setMessage({
          text: "You can select a maximum of 3 projects.",
          type: "error",
        });
        return prev;
      }

      if (prev.length > 0 && project.department?.name !== prev[0].department?.name) {
        setMessage({
          text: "You can only select projects from the same department.",
          type: "error",
        });
        return prev;
      }

      return [...prev, project];
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(selectedProjects);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedProjects(items);

    const listItems = Array.from(document.querySelectorAll('.selected-item'));
    const movingUp = result.destination.index < result.source.index;

    listItems.forEach((item, index) => {
      const itemId = item.dataset.id;
      const itemNode = item;

      if (itemId === reorderedItem.id.toString()) {
        gsap.fromTo(itemNode,
          {
            y: movingUp ? -30 : 30,
            backgroundColor: 'rgba(52, 152, 219, 0.2)',
            scale: 1.02
          },
          {
            y: 0,
            backgroundColor: 'white',
            scale: 1,
            duration: 0.3,
            ease: "power2.out"
          }
        );
      } else if (
        (movingUp && index >= result.destination.index && index < result.source.index) ||
        (!movingUp && index > result.source.index && index <= result.destination.index)
      ) {
        gsap.fromTo(itemNode,
          { y: movingUp ? 30 : -30 },
          {
            y: 0,
            duration: 0.3,
            ease: "power2.out"
          }
        );
      }
    });
  };

  const submitPreferences = async () => {
    if (selectedProjects.length === 0) {
      setMessage({
        text: "Please select at least one project.",
        type: "error",
      });
      return;
    }

    try {
      setIsLoading(true);
      setMessage({ text: "", type: "" });

      const body = {
        pref1_id: selectedProjects[0]?.id || null,
        pref2_id: selectedProjects[1]?.id || null,
        pref3_id: selectedProjects[2]?.id || null,
      };

      const res = await authFetch("/api/students/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setMessage({
          text: "Preferences submitted successfully!",
          type: "success",
        });
      } else {
        const err = await res.json();
        throw new Error(err.detail || "Failed to submit preferences");
      }
    } catch (err) {
      setMessage({
        text: err.message || "Network error",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryPayment = () => {
    navigate("/payment2");
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    navigate("/login");
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ display: "flex" }}>
        <aside className="sidebar">
          <div className="profile-pic-placeholder"></div>
          <nav>
            <button disabled><FontAwesomeIcon icon={faHome} /><span>Dashboard</span></button>
            <button disabled><FontAwesomeIcon icon={faUser} /><span>Profile</span></button>
            <button disabled><FontAwesomeIcon icon={faSignOutAlt} /><span>Logout</span></button>
          </nav>
        </aside>
        <div className="preferences-container">
          <h1>Project Preferences</h1>
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  // If payment failed
  if (adminConf && paymentFailed) {
    return (
      <div style={{ display: "flex" }}>
        <aside className="sidebar">
          <nav>
            <button onClick={() => navigate("/candidatedashboard")}>
              <FontAwesomeIcon icon={faHome} />
              <span>Dashboard</span>
            </button>
            <button onClick={() => navigate("/candidate_profile")}>
              <FontAwesomeIcon icon={faUser} />
              <span>Profile</span>
            </button>
            <button onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Logout</span>
            </button>
          </nav>
        </aside>

        <div className="preferences-container">
          <h1>Payment Required</h1>
          <div className="error-message-card" style={{ textAlign: 'center', padding: '40px' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" style={{ color: '#dc3545', marginBottom: '20px' }} />
            <h2>Payment Failed or Not Completed</h2>
            <p>{allottedProjectError || "Please complete the offer payment to view your allotted project."}</p>
            <div style={{ marginTop: '30px' }}>
              <button 
                onClick={handleRetryPayment}
                className="submit-btn"
                style={{ backgroundColor: '#007bff', marginRight: '15px' }}
              >
                <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px' }} />
                Retry Payment
              </button>
              <button 
                onClick={() => navigate("/candidatedashboard")}
                className="upload-new-btn"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If admin has confirmed AND has allotted project (payment successful), show allotted project details
  if (adminConf && allottedProject && paymentCompleted) {
    return (
      <div style={{ display: "flex" }}>
        <aside className="sidebar">
          <nav>
            <button onClick={() => navigate("/candidatedashboard")}>
              <FontAwesomeIcon icon={faHome} />
              <span>Dashboard</span>
            </button>
            <button onClick={() => navigate("/candidate_profile")}>
              <FontAwesomeIcon icon={faUser} />
              <span>Profile</span>
            </button>
            <button onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Logout</span>
            </button>
          </nav>
        </aside>

        <div className="preferences-container">
          <h1>Your Allotted Project</h1>

          <div className="allotted-project-card">
            <h2>{allottedProject.title || "Untitled Project"}</h2>
            <div className="project-details">
              <p><strong>Description:</strong> {allottedProject.description || "No description available"}</p>
              <p><strong>Department:</strong> {allottedProject.department?.name || "N/A"}</p>
              <p>
                <p>
  <strong>Professor:</strong> {allottedProject.professor?.name || "N/A"}
  {/* Fix: Display professor email with better error handling */}
  {(() => {
    // Try to get email from different possible locations in the data structure
    const professorEmail = 
      allottedProject.professor?.email || 
      allottedProject.professor?.user?.email || 
      allottedProject.professor_email ||
      null;
    
    console.log("Professor data structure:", allottedProject.professor);
    console.log("Extracted email:", professorEmail);
    
    return professorEmail && (
      <span style={{  marginTop: '8px', fontSize: '0.95em', color: '#555' }}>
        <FontAwesomeIcon icon={faEnvelope} style={{  color: '#007bff' }} />
        Email: <a href={`mailto:${professorEmail}`} style={{ color: '#007bff', textDecoration: 'none' }}>
          {professorEmail}
        </a>
      </span>
    );
  })()}
</p>
              </p>
              <p><strong>Mode:</strong> {allottedProject.mode || "N/A"}</p>
              <p><strong>Duration:</strong> {allottedProject.duration || "N/A"}</p>
              {allottedProject.prerequisites && (
                <p><strong>Prerequisites:</strong> {allottedProject.prerequisites}</p>
              )}
              <p><strong>Project Report Status:</strong> {hasExistingReport ? "Submitted" : "Not Submitted"}</p>
              {offerPaymentStatus && (
                <p><strong>Payment Status:</strong>
                  <span className={`payment-status ${offerPaymentStatus}`}>
                    {offerPaymentStatus.charAt(0).toUpperCase() + offerPaymentStatus.slice(1)}
                  </span>
                </p>
              )}
            </div>

            {/* Expected Joining Date Section */}
            <div className="expected-joining-section">
              <h3>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '10px' }} />
                Expected Joining Date
              </h3>

                  <p>Kindly check with your faculty mentor before setting the start date </p>
                  
              <p className="warning-text">Don't select a public holiday or Saturday/Sunday as joining date.</p>
              {allottedProject.expected_joining_date && !isEditingJoiningDate ? (
                <div className="joining-date-display">
                  <p><strong>Your selected joining date:</strong></p>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      
                      <span className="date-set">
                        {allottedProject.expected_joining_date}
                      </span>
                    </div>
                    <FontAwesomeIcon 
                      icon={faEdit} 
                      onClick={() => {
                        setIsEditingJoiningDate(true);
                        setExpectedJoiningDate(allottedProject.expected_joining_date);
                      }}
                      style={{ 
                        color: '#007bff', 
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        // marginTop: '8px'
                      }}
                      title="Edit joining date"
                    />
                  </div>
                </div>
              ) : (
                <div className="joining-date-input-container">
                  <p>Please add your expected joining date after contacting your professor:</p>
                  <div className="joining-date-input">
                    <input
                      type="date"
                      id="expectedJoiningDate"
                      value={expectedJoiningDate}
                      onChange={(e) => setExpectedJoiningDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="date-picker"
                    />
                    <button
                      onClick={handleSetExpectedJoiningDate}
                      disabled={!expectedJoiningDate || isSubmittingDate}
                      className="submit-date-btn"
                    >
                      {isSubmittingDate ? 'Submitting...' : 'Confirm Joining Date'}
                    </button>
                  </div>
                </div>
              )}

              {dateSubmitMessage.text && (
                <p className={`joining-date-message ${dateSubmitMessage.type}`}>
                  {dateSubmitMessage.text}
                </p>
              )}
            </div>

            {/* Project Report Upload Section */}
            <div className="project-report-section">
              <h3>
                <FontAwesomeIcon icon={faFileUpload} style={{ marginRight: '10px' }} />
                Project Report Upload
              </h3>
              <p>You can upload the project report once your internship is completed.</p>

              {/* Show existing report status */}
              {checkingReport ? (
                <div className="checking-report">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Checking for existing report...</span>
                </div>
              ) : hasExistingReport ? (
                <div className="existing-report-full">
                  <div className="report-exists-message">
                    <FontAwesomeIcon icon={faCheck} style={{ color: '#28a745', marginRight: '10px' }} />
                    <span>You have already submitted a project report.</span>
                  </div>

                  {allottedProject.report_filename && (
                    <div className="report-details">
                      <p><strong>Filename:</strong> {allottedProject.report_filename}</p>
                    </div>
                  )}

                  <div className="report-actions">
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const token = localStorage.getItem("access_token");
                          const response = await fetch("/api/students/project_report", {
                            method: "GET",
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          });

                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = allottedProject.report_filename || 'project_report.pdf';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } else {
                            console.error('Failed to download report');
                          }
                        } catch (error) {
                          console.error('Error downloading report:', error);
                        }
                      }}
                      className="download-report-btn"
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                      Download Report
                    </button>

                    <button
                      onClick={() => {
                        // Allow re-upload by clearing the existing report flag
                        setHasExistingReport(false);
                        setReportFile(null);
                      }}
                      className="upload-new-btn"
                    >
                      <FontAwesomeIcon icon={faUpload} />
                      Upload New Report
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="info-text">Please upload your project report (PDF, DOC, DOCX only)</p>

                  <form onSubmit={handleReportUpload} className="upload-form">
                    <div className="form-group">
                      <label htmlFor="projectReport" className="file-upload-label">
                        <FontAwesomeIcon icon={faFileAlt} className="upload-icon" />
                        <span>{reportFile ? reportFile.name : "Choose Project Report"}</span>
                        <input
                          id="projectReport"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleReportFileChange}
                          disabled={uploading}
                          className="file-input"
                          required
                        />
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="upload-btn"
                      disabled={uploading || !reportFile}
                    >
                      {uploading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faUpload} />
                          <span>Upload Report</span>
                        </>
                      )}
                    </button>

                    {uploadMsg && <div className="success-message">{uploadMsg}</div>}
                    {uploadError && <div className="error-message">{uploadError}</div>}
                  </form>
                </>
              )}
            </div>

            <div className="confirmation-message">
              <FontAwesomeIcon icon={faCheck} style={{ marginRight: '10px' }} />
              Your project has been confirmed by the admin. Please contact your professor for further instructions.
              Please report to the CCD on the first day of the project.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If admin confirmed but payment not started or in progress
  if (adminConf && !paymentCompleted && !paymentFailed && !allottedProject) {
    return (
      <div style={{ display: "flex" }}>
        <aside className="sidebar">
          <nav>
            <button onClick={() => navigate("/candidatedashboard")}>
              <FontAwesomeIcon icon={faHome} />
              <span>Dashboard</span>
            </button>
            <button onClick={() => navigate("/candidate_profile")}>
              <FontAwesomeIcon icon={faUser} />
              <span>Profile</span>
            </button>
            <button onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Logout</span>
            </button>
          </nav>
        </aside>

        <div className="preferences-container">
          <h1>Project Preferences</h1>
          <div className="info-message">
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '10px' }} />
            Admin has confirmed your eligibility. Please complete the offer payment to view your allotted project.
          </div>
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <button 
              onClick={handleRetryPayment}
              className="submit-btn"
            >
              <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px' }} />
              Proceed to Payment
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal preferences selection flow (admin not confirmed yet)
  return (
    <div style={{ display: "flex" }}>
      <aside className="sidebar">
        <nav>
          <button onClick={() => navigate("/candidatedashboard")}>
            <FontAwesomeIcon icon={faHome} />
            <span>Dashboard</span>
          </button>
          <button onClick={() => navigate("/candidate_profile")}>
            <FontAwesomeIcon icon={faUser} />
            <span>Profile</span>
          </button>
          <button onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </nav>
      </aside>

      <div className="preferences-container">
        <h1>Project Preferences</h1>
        <h2 className="subtitle">
          You can drag and change the preference for your project.
          You can select projects from only one department (maximum 3 projects)
        </h2>

        {selectedProjects.length > 0 && (
          <div className="info-message">
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '10px' }} />
            You have {selectedProjects.length} project(s) selected. Drag to reorder your preferences.
          </div>
        )}

        <div className="filters-section">
          <div className="filter-row">
            <div className="filter-group">
              <label>Department <span style={{ color: 'red' }}>*</span></label>
              <select
                name="department"
                value={filters.department}
                onChange={handleFilterChange}
                disabled={selectedProjects.length > 0}
                required
                style={!filters.department ? { borderColor: '#ff6b6b' } : {}}
              >
                <option value="">-- Select a Department First --</option>
                {departments.map((dept, index) => (
                  <option key={index} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              {!filters.department && (
                <small style={{ color: '#ff6b6b', marginTop: '5px' }}>
                  Please select a department to view available projects
                </small>
              )}
            </div>
            <div className="filter-group">
              <label>Mode:</label>
              <select name="mode" value={filters.mode} onChange={handleFilterChange} disabled={!filters.department}>
                <option value="">All Modes</option>
                {modes.map((mode, index) => (
                  <option key={index} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Duration:</label>
              <select name="duration" value={filters.duration} onChange={handleFilterChange} disabled={!filters.department}>
                <option value="">All Durations</option>
                {durations.map((duration, index) => (
                  <option key={index} value={duration}>
                    {duration}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <SearchableDropdown
                options={professors}
                value={filters.professor}
                onChange={handleFilterChange}
                placeholder="Search professor..."
                disabled={!filters.department}
                label="Professor:"
              />
            </div>
          </div>
          <div className="search-group">
            <input
              type="text"
              placeholder="Search projects by title..."
              value={filters.search}
              onChange={handleSearchChange}
              disabled={!filters.department}
            />
          </div>
        </div>

        <div className="projects-section">
          <div className="available-projects">
            <h2>
              Available Projects
              {filters.department ? ` (${filteredProjects.length})` : ' (0)'}
            </h2>
            <div className="projects-table-container">
              <table className="projects-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Department</th>
                    <th>Professor</th>
                    <th>Mode</th>
                    <th>Duration</th>
                    <th>People Applied</th>
                    <th>Vacancies</th>
                  </tr>
                </thead>
                <tbody>
                  {!filters.department ? (
                    <tr>
                      <td colSpan="9" className="no-data" style={{ backgroundColor: '#f9f9f9' }}>
                        <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '10px', color: '#ff6b6b' }} />
                        Please select a department first to view available projects
                      </td>
                    </tr>
                  ) : filteredProjects.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-data">No projects found matching your filters.</td>
                    </tr>
                  ) : (
                    filteredProjects.map((project) => {
                      const isSelected = selectedProjects.some((p) => p.id === project.id);
                      const isDisabled =
                        !filters.department ||
                        (selectedProjects.length >= 3 && !isSelected) ||
                        (selectedProjects.length > 0 &&
                          project.department?.name !== selectedProjects[0]?.department?.name);

                      return (
                        <tr
                          key={project.id}
                          className={isSelected ? "selected-row" : ""}
                          style={isDisabled && !isSelected ? { opacity: 0.6, backgroundColor: '#f5f5f5' } : {}}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleProjectSelection(project)}
                              disabled={isDisabled}
                              title={
                                !filters.department ? "Select a department first" :
                                  (selectedProjects.length >= 3 && !isSelected) ? "Maximum 3 projects can be selected" :
                                    (selectedProjects.length > 0 &&
                                      project.department?.name !== selectedProjects[0]?.department?.name)
                                      ? `Only projects from ${selectedProjects[0]?.department?.name} department can be selected`
                                      : ""
                              }
                            />
                          </td>
                          <td>{project.title || "N/A"}</td>
                          <td>{project.description || "N/A"}</td>
                          <td>{project.department?.name || "N/A"}</td>
                          <td>{project.professor?.name || "N/A"}</td>
                          <td>{project.mode || "N/A"}</td>
                          <td>{project.duration || "N/A"}</td>
                          <td>{project.applied_count || 0}</td>
                          <td>{project.vacancy_remaining || 0}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="selected-projects">
            <h2>Selected Projects ({selectedProjects.length}/3)</h2>
            {selectedProjects.length === 0 ? (
              <div className="empty-selection">
                <FontAwesomeIcon icon={faList} size="3x" style={{ marginBottom: '15px', color: '#ccc' }} />
                <p>No projects selected yet.</p>
                <p>Select a department first, then select projects (max 3).</p>
              </div>
            ) : (
              <div className="selected-list">
                <div className="department-warning">
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
                  Please make sure to submit the preferences before the deadline.
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="selected-projects-list">
                    {(provided) => (
                      <ol
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="draggable-list"
                      >
                        {selectedProjects.map((project, index) => (
                          <Draggable
                            key={project.id}
                            draggableId={project.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`selected-item ${snapshot.isDragging ? "dragging" : ""}`}
                                data-id={project.id.toString()}
                              >
                                <div className="drag-handle">
                                  <FontAwesomeIcon icon={faList} />
                                </div>
                                <div className="selected-content">
                                  <div className="selected-rank">Preference {index + 1}</div>
                                  <div className="selected-title">{project.title || "N/A"}</div>
                                  <div className="selected-prof">
                                    {project.professor?.name || "N/A"}
                                  </div>
                                </div>
                                <button
                                  onClick={() => toggleProjectSelection(project)}
                                  className="remove-btn"
                                  title="Remove"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </li>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </ol>
                    )}
                  </Droppable>
                </DragDropContext>

                <button
                  onClick={submitPreferences}
                  className="submit-btn"
                  disabled={isLoading || selectedProjects.length === 0}
                >
                  {isLoading ? "Submitting..." : "Submit Preferences"}
                </button>
              </div>
            )}
          </div>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' && <FontAwesomeIcon icon={faCheck} style={{ marginRight: '10px' }} />}
            {message.type === 'error' && <FontAwesomeIcon icon={faTimes} style={{ marginRight: '10px' }} />}
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidatePreferences;
